import React, { useEffect, useState } from 'react'
import {
  Btn,
  ConfirmDialog,
  CustomDropdown,
  CustomInputTextArea,
  CustomModal,
  DateTime,
  InputPhone,
  InputText,
  NumeroDecimal,
  SectionTitle,
} from '../../../../../components'
import { getCfops } from '../../../../../services/api/https/settings/cfop.http'
import { postEmitCteSubstituicao } from '../../../../../services/api/https/deliveries/cte.http'
import { somenteDigitos } from '../../../../../services/util/documento.service'
import RadioGroup from '../EmitirCte/CteSemNota/RadioGroup'
import { formatarChaveCte } from '../EmissaoCteSubcontratacao/cteSubcontratacao.util'
import ValoresFiscaisCte from '../ValoresFiscaisCte/ValoresFiscaisCte'
import {
  VALORES_FISCAIS_INICIAIS,
  montarComponentesValor,
  novoComponente,
} from '../ValoresFiscaisCte/valoresFiscaisCte.util'
import {
  MOTIVO_SUBSTITUICAO_MAX,
  MOTIVO_SUBSTITUICAO_MIN,
  MOTIVO_TROCA_TOMADOR,
  URL_PORTAL_DESACORDO,
  aceitaTrocaTomador,
  descreverTomador,
  erroSubstituicao,
  estadoInicialSubstituicao,
  isCteDeSubcontratacao,
  nomeTomador,
  observacaoCompleta,
  opcoesTomador,
  prazoVencido,
  resumoAlteracoes,
  tomadorDoCte,
  valorMaiorQueOriginal,
} from './cteSubstituicao.util'

const estadoVazio = { cfop_id: null, ...VALORES_FISCAIS_INICIAIS, observacoes: '' }

const estiloAviso = {
  padding: '8px 12px',
  borderRadius: 3,
  background: '#FFF3CD',
  border: '1px solid #FFEEBA',
  color: '#856404',
  fontSize: '0.85rem',
}

const estiloDica = { color: 'gray', fontSize: 12 }

// CTe de Substituição: corrige valores, impostos ou o tomador de um CTe autorizado (linha da tela 3.18).
// `dados` vem de GET /cte/substituicao/dados: prazos legais e componentes do valor do CTe original.
export default function ModalEmitirCteSubstituicao({
  visible,
  cte,
  dados,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(estadoVazio)
  const [componentes, setComponentes] = useState([])
  const [cfops, setCfops] = useState([])
  const [motivo, setMotivo] = useState('')
  const [tomador, setTomador] = useState('0')
  // Telefone do tomador (emitente do CTe de origem): exigido pela Focus no CTe de Subcontratação
  const [telefoneTomador, setTelefoneTomador] = useState('')
  const [confirmacaoVisivel, setConfirmacaoVisivel] = useState(false)
  // Os campos de valor recalculam os dependentes quando recebem valor por props (a base do ICMS segue
  // o valor líquido, por exemplo): o formulário só é montado com os valores do CTe original já no
  // state, para o pré-preenchimento sair igual ao documento a substituir
  const [valoresCarregados, setValoresCarregados] = useState(false)

  useEffect(() => {
    if (!visible || !cte) {
      setValoresCarregados(false)
      return
    }
    setState(estadoInicialSubstituicao(cte))
    setComponentes(
      ((dados && dados.componentes) || []).map(componente =>
        novoComponente(componente.nome, componente.valor),
      ),
    )
    setMotivo('')
    setTomador(tomadorDoCte(cte))
    setTelefoneTomador(cte.cte_origem_telefone || '')
    setConfirmacaoVisivel(false)
    setValoresCarregados(true)
  }, [visible, cte, dados])

  useEffect(() => {
    async function fetchCfops() {
      try {
        const lista = await getCfops()
        setCfops(
          lista.map(cfop => ({ ...cfop, codigoNatureza: `${cfop.codigo} - ${cfop.natureza}` })),
        )
      } catch (error) {
        onError(error)
      }
    }
    if (visible && !cfops.length) fetchCfops()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  if (!cte) return null

  // Vars
  const prazos = dados || {}
  const tomadorOriginal = tomadorDoCte(cte)
  const permiteTrocaTomador = aceitaTrocaTomador(cte)
  const subcontratacao = isCteDeSubcontratacao(cte)
  const cfopSelecionado = cfops.find(cfop => cfop.id === state.cfop_id) || null
  const alteracoes = resumoAlteracoes(cte, state, tomador)

  // Events
  const onTomadorChange = novoTomador => {
    setTomador(novoTomador)
    setMotivo(atual => {
      if (novoTomador !== tomadorOriginal && !atual.trim()) return MOTIVO_TROCA_TOMADOR
      if (novoTomador === tomadorOriginal && atual === MOTIVO_TROCA_TOMADOR) return ''
      return atual
    })
  }

  const onBtnSalvarClicked = () => {
    const erro = erroSubstituicao({ cte, state, motivo, cfopSelecionado, telefoneTomador })
    if (erro) {
      onError(new Error(erro))
      return
    }
    setConfirmacaoVisivel(true)
  }

  const onConfirmar = async () => {
    try {
      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      await postEmitCteSubstituicao({
        ...state,
        cte_id: cte.id,
        motivo: motivo.trim(),
        ...(permiteTrocaTomador ? { tomador } : {}),
        ...(subcontratacao ? { telefone_tomador: somenteDigitos(telefoneTomador) } : {}),
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      setConfirmacaoVisivel(false)
      onModalSave()
    } catch (error) {
      setConfirmacaoVisivel(false)
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const renderCampo = (label, valor) => (
    <div style={{ fontSize: 13, paddingTop: 2, paddingBottom: 2 }}>
      <b>{label}:</b> {valor}
    </div>
  )

  const renderValor = (valor, moeda) =>
    moeda ? (
      <>
        R$ <NumeroDecimal>{valor}</NumeroDecimal>
      </>
    ) : (
      valor
    )

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe de Substituição'}
      width={900}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        {/* CTe a substituir (somente leitura): identificação à esquerda, prestação e valores à direita */}
        <div className="p-col-12">
          <SectionTitle title="CTe a Substituir" />
        </div>
        <div className="p-col-12">
          <div
            className="p-grid"
            style={{
              backgroundColor: '#e3f2fd',
              padding: '8px 10px',
              borderRadius: '5px',
              margin: 0,
            }}
          >
            <div className="p-md-6 p-col-12">
              {renderCampo(
                'CTe',
                `nº ${cte.numero} · série ${cte.serie} · ${cte.tipo_documento_descricao}`,
              )}
              {renderCampo('Chave', formatarChaveCte(somenteDigitos(cte.chave)))}
              {renderCampo('Emissão', <DateTime>{cte.data_emissao}</DateTime>)}
              {renderCampo('Autorização', <DateTime>{prazos.data_autorizacao}</DateTime>)}
              {renderCampo('Emissor', cte.emissor_razao_social)}
              {renderCampo('Tomador', descreverTomador(cte))}
            </div>
            <div className="p-md-6 p-col-12">
              {renderCampo('Empresa', cte.company_name)}
              {renderCampo(
                'Cliente',
                `${cte.client_name}${cte.client_city ? ` - ${cte.client_city}` : ''}`,
              )}
              {renderCampo(
                'Doc. referência',
                `${cte.documento_referencia_tipo}: ${cte.documento_referencia || '-'}`,
              )}
              {renderCampo('Tipo de serviço', cte.tipo_servico_descricao)}
              {renderCampo('Valor do frete', renderValor(cte.valor_total, true))}
              {renderCampo('ICMS', renderValor(cte.icms_valor, true))}
            </div>
          </div>
        </div>

        {/* O que precisa existir fora do sistema para a SEFAZ autorizar */}
        <div className="p-col-12">
          <div style={estiloAviso}>
            <div>
              <b>Antes de emitir:</b> o tomador do CTe original, <b>{nomeTomador(cte)}</b>, precisa
              ter registrado o evento <b>Prestação do Serviço em Desacordo</b> até{' '}
              <b>
                <DateTime>{prazos.limite_desacordo}</DateTime>
              </b>
              . Sem o evento a SEFAZ rejeita a substituição.
            </div>
            <div style={{ marginTop: 5 }}>
              A substituição precisa ser autorizada até{' '}
              <b>
                <DateTime>{prazos.limite_substituicao}</DateTime>
              </b>
              . Depois de autorizado, o CTe de Substituição <b>não pode ser cancelado</b> e este CTe
              não poderá ser substituído de novo.
            </div>
            {prazoVencido(prazos.limite_desacordo) && (
              <div style={{ marginTop: 5, fontWeight: 'bold' }}>
                O prazo do evento já terminou: a substituição só será autorizada se o tomador o
                registrou dentro do prazo.
              </div>
            )}
            {prazos.prazo_estimado && (
              <div style={{ marginTop: 5 }}>
                Prazos estimados pela data de emissão: a Focus não informou a data de autorização.
              </div>
            )}
            <div style={{ marginTop: 5 }}>
              <a href={URL_PORTAL_DESACORDO} target="_blank" rel="noopener noreferrer">
                Portal da SVRS para o tomador registrar o evento
              </a>
            </div>
          </div>
        </div>

        {/* Dados da emissão */}
        <div className="p-col-12">
          <SectionTitle title="Dados da Emissão" />
        </div>
        <div className={subcontratacao ? 'p-md-8 p-col-12' : 'p-col-12'}>
          <CustomDropdown
            optionLabel="codigoNatureza"
            options={cfops}
            value={cfopSelecionado}
            label="CFOP:*"
            filter={true}
            onChange={value => setState(prevState => ({ ...prevState, cfop_id: value.id }))}
            tooltip={'Código Fiscal de Operações e Prestações'}
          />
        </div>
        {subcontratacao && (
          <div className="p-md-4 p-col-12">
            <InputPhone
              label="Telefone do tomador:*"
              marginTop={10}
              mask="(99)9999-9999?9"
              autoClear={false}
              value={telefoneTomador}
              onChange={value => setTelefoneTomador(value || '')}
            />
            <span style={estiloDica}>
              Emitente do CTe de origem, tomador do CTe de Subcontratação
            </span>
          </div>
        )}
        <div className="p-col-12">
          <InputText
            label="Motivo da substituição:*"
            marginTop={10}
            value={motivo}
            maxLength={MOTIVO_SUBSTITUICAO_MAX}
            onChange={value => setMotivo(value)}
          />
          <span style={estiloDica}>
            Entra na expressão legal das observações do CTe (de {MOTIVO_SUBSTITUICAO_MIN} a{' '}
            {MOTIVO_SUBSTITUICAO_MAX} caracteres). Ex.: valor do frete informado a maior
          </span>
        </div>

        {permiteTrocaTomador && (
          <div className="p-col-12">
            <SectionTitle title="Tomador do Serviço" />
            <div style={{ marginTop: 5 }}>
              <RadioGroup
                nome="tomador-substituicao-cte"
                items={opcoesTomador(cte)}
                value={tomador}
                onChange={onTomadorChange}
              />
            </div>
            <span style={estiloDica}>
              {tomador !== tomadorOriginal
                ? 'Troca de tomador: o evento de desacordo continua sendo registrado pelo tomador do CTe original.'
                : 'Altere somente se o tomador do CTe original foi informado errado.'}
            </span>
          </div>
        )}

        {valoresCarregados && (
          <ValoresFiscaisCte
            state={state}
            onStateChange={setState}
            componentes={componentes}
            onComponentesChange={setComponentes}
            onError={onError}
          />
        )}
        {valorMaiorQueOriginal(cte, state) && (
          <div className="p-col-12" style={{ paddingTop: 0 }}>
            <div style={{ color: '#e67e22', fontWeight: 'bold' }}>
              Atenção! O novo valor do frete é maior que o do CTe original
            </div>
            <div style={{ color: '#e67e22', fontSize: 12 }}>
              * Para valor cobrado a menor a legislação prevê o CTe Complementar. Apenas um alerta!
              Não impede a emissão.
            </div>
          </div>
        )}

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={3}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
          <span style={estiloDica}>
            Texto enviado à SEFAZ: {observacaoCompleta(cte, motivo, state.observacoes)}
          </span>
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>

      {/* A emissão não tem volta: o substituto não pode ser cancelado nem o CTe substituído de novo */}
      <ConfirmDialog
        title="Emitir CTe de Substituição"
        isVisible={confirmacaoVisivel}
        disabled={isLoading}
        content={
          <>
            <div>
              Confirma a substituição do CTe nº <b>{cte.numero}</b>?
            </div>
            {alteracoes.length ? (
              <ul style={{ paddingLeft: 18, margin: '8px 0' }}>
                {alteracoes.map(alteracao => (
                  <li key={alteracao.rotulo}>
                    {alteracao.rotulo}: {renderValor(alteracao.antes, alteracao.moeda)} →{' '}
                    <b>{renderValor(alteracao.depois, alteracao.moeda)}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#e67e22', margin: '8px 0' }}>
                Nenhum valor foi alterado em relação ao CTe original.
              </div>
            )}
            <div>
              Depois de autorizado, o CTe de Substituição <b>não pode ser cancelado</b> e o CTe nº{' '}
              {cte.numero} não poderá ser substituído de novo.
            </div>
          </>
        }
        onOk={onConfirmar}
        onCancelar={() => setConfirmacaoVisivel(false)}
        okLabel="Emitir"
        cancelLabel="Voltar"
      />
    </CustomModal>
  )
}
